import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useStores, useTrendsDaily, useTrendsMonthly } from "../hooks/useApi";

interface Store {
  id: string;
  code: string;
  name: string;
}

// Numeric aggregates arrive as strings (Prisma Decimal serialization).
interface DailyRow {
  storeId: string;
  store_code: string;
  store_name: string;
  date: string;
  variance_value: string;
  counted: number;
}

interface MonthlyRow {
  storeId: string;
  store_code: string;
  store_name: string;
  month: string;
  loss_value: string;
}

const LINE_COLORS = [
  "#007bff",
  "#dc3545",
  "#28a745",
  "#fd7e14",
  "#6f42c1",
  "#20c997",
  "#e83e8c",
  "#795548",
  "#607d8b",
  "#ffc107",
];

export function AdminTrends() {
  const [storeFilter, setStoreFilter] = useState("");
  const { data: stores } = useStores();
  const { data: daily, isLoading: dailyLoading } = useTrendsDaily(30);
  const { data: monthly, isLoading: monthlyLoading } = useTrendsMonthly(6);

  const storeList = (stores as Store[]) || [];

  const dailyRows = ((daily as DailyRow[]) || []).filter(
    (r) => !storeFilter || r.storeId === storeFilter
  );
  const monthlyRows = ((monthly as MonthlyRow[]) || []).filter(
    (r) => !storeFilter || r.storeId === storeFilter
  );

  // Pivot daily rows: one point per date, one series per store code.
  const dailyChart = useMemo(() => {
    const byDate = new Map<string, Record<string, number | string>>();
    for (const r of dailyRows) {
      const point = byDate.get(r.date) || { date: r.date.slice(5) };
      point[r.store_code] = Math.round(parseFloat(r.variance_value) * 100) / 100;
      byDate.set(r.date, point);
    }
    return [...byDate.values()];
  }, [dailyRows]);

  // Pivot monthly rows the same way.
  const monthlyChart = useMemo(() => {
    const byMonth = new Map<string, Record<string, number | string>>();
    for (const r of monthlyRows) {
      const point = byMonth.get(r.month) || { month: r.month.slice(0, 7) };
      point[r.store_code] = Math.round(parseFloat(r.loss_value) * 100) / 100;
      byMonth.set(r.month, point);
    }
    return [...byMonth.values()];
  }, [monthlyRows]);

  const seriesCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const r of dailyRows) codes.add(r.store_code);
    for (const r of monthlyRows) codes.add(r.store_code);
    return [...codes].sort();
  }, [dailyRows, monthlyRows]);

  return (
    <div style={{ padding: "1rem" }}>
      <h1>Trends</h1>
      <p style={{ color: "#666", margin: "0.25rem 0 1rem" }}>
        Variance value = variance × unit price. Negative means stock loss.
      </p>

      <label style={{ display: "inline-flex", flexDirection: "column", gap: "0.25rem", marginBottom: "1.5rem" }}>
        <span style={{ fontSize: "0.875rem", color: "#666" }}>Store</span>
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          style={{ padding: "0.5rem", minWidth: "220px" }}
        >
          <option value="">All stores</option>
          {storeList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
      </label>

      <ChartCard title="Daily variance value — last 30 days">
        {dailyLoading ? (
          <p>Loading…</p>
        ) : dailyChart.length === 0 ? (
          <Empty />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={dailyChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => `R ${Number(v).toLocaleString("en-ZA")}`} />
              <Legend />
              {seriesCodes.map((code, i) => (
                <Line
                  key={code}
                  type="monotone"
                  dataKey={code}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Monthly stock-loss value — last 6 months">
        {monthlyLoading ? (
          <p>Loading…</p>
        ) : monthlyChart.length === 0 ? (
          <Empty />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthlyChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => `R ${Number(v).toLocaleString("en-ZA")}`} />
              <Legend />
              {seriesCodes.map((code, i) => (
                <Bar
                  key={code}
                  dataKey={code}
                  fill={LINE_COLORS[i % LINE_COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: "8px",
        padding: "1rem",
        marginBottom: "1.5rem",
      }}
    >
      <h2 style={{ fontSize: "1.05rem", marginBottom: "1rem" }}>{title}</h2>
      {children}
    </div>
  );
}

function Empty() {
  return <p style={{ color: "#666" }}>No data yet — trends appear once physical counts are entered.</p>;
}
